<template>
  <div
    v-if="visible && popup"
    class="promo-popup"
  >
    <div
      class="promo-popup-backdrop"
      @click="close"
    />
    <div
      class="promo-popup-dialog"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="popup.title ? 'promo-popup-title' : undefined"
      :aria-label="popup.title ? undefined : 'Khuyến mãi'"
    >
      <button
        type="button"
        class="promo-popup-close"
        aria-label="Đóng"
        @click="close"
      >
        <a-icon type="close" />
      </button>

      <aside :class="['promo-popup-hero', { 'has-banner': Boolean(popup.banner) }]">
        <img
          v-if="popup.banner"
          :src="popup.banner"
          :alt="popup.title || 'Khuyến mãi'"
          class="promo-popup-banner"
        >
        <span class="promo-popup-eyebrow">{{ popup.eyebrow }}</span>
        <div class="promo-popup-hero-main">
          <template v-if="popup.maxFollow">
            <strong class="promo-popup-hero-value">+{{ formatNumber(popup.maxFollow) }}</strong>
            <span class="promo-popup-hero-label">Follow cho shop Shopee</span>
          </template>
          <strong
            v-else
            class="promo-popup-hero-value promo-popup-hero-value--text"
          >
            Chỉ từ {{ formatPrice(popup.minPrice) }}
          </strong>
        </div>
        <ul
          v-if="hasBenefits"
          class="promo-popup-benefits"
          role="list"
        >
          <li v-if="showFollowRange">
            <a-icon
              type="check"
              class="promo-popup-benefit-icon"
            />
            <span>Gói từ {{ formatNumber(popup.minFollow) }} đến {{ formatNumber(popup.maxFollow) }} Follow</span>
          </li>
          <li v-if="popup.maxDiscount > 0">
            <a-icon
              type="check"
              class="promo-popup-benefit-icon"
            />
            <span>Giảm đến {{ popup.maxDiscount }}% so với giá gốc</span>
          </li>
        </ul>
      </aside>

      <div class="promo-popup-body">
        <h2
          v-if="popup.title"
          id="promo-popup-title"
          class="promo-popup-title"
        >
          {{ popup.title }}
        </h2>
        <p
          v-if="popup.description"
          class="promo-popup-desc"
        >
          {{ popup.description }}
        </p>

        <ul
          v-if="popup.plans && popup.plans.length"
          class="promo-popup-plans"
          role="list"
        >
          <li
            v-for="plan in popup.plans"
            :key="plan.code"
            :class="['promo-popup-plan', { 'is-featured': plan.featured }]"
          >
            <span
              v-if="plan.featured"
              class="promo-popup-plan-flag"
            >
              Tiết kiệm nhất
            </span>
            <div class="promo-popup-plan-name">
              <template v-if="plan.follow">
                <span class="promo-popup-plan-follow">{{ formatNumber(plan.follow) }}</span>
                <span class="promo-popup-plan-unit">FOLLOW</span>
              </template>
              <span
                v-else
                class="promo-popup-plan-follow promo-popup-plan-follow--text"
              >
                {{ plan.title }}
              </span>
            </div>
            <div class="promo-popup-plan-pricing">
              <span class="promo-popup-plan-price">{{ formatPrice(plan.price) }}</span>
              <span
                v-if="plan.discount > 0"
                class="promo-popup-plan-meta"
              >
                <del>{{ formatPrice(plan.priceOrigin) }}</del>
                <span class="promo-popup-plan-off">-{{ plan.discount }}%</span>
              </span>
            </div>
          </li>
        </ul>

        <div class="promo-popup-action">
          <a
            :href="popup.href"
            target="_blank"
            rel="noopener"
            class="promo-popup-cta"
            @click="close"
          >
            <span>{{ popup.ctaLabel }}</span>
            <a-icon type="arrow-right" />
          </a>
          <span class="promo-popup-note">Cần tư vấn trước khi đặt gói?</span>
          <div class="promo-popup-contacts">
            <a
              :href="zaloHref"
              target="_blank"
              rel="noopener noreferrer"
              class="promo-popup-contact"
            >
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="currentColor"
                aria-hidden="true"
                class="promo-popup-contact-icon"
              >
                <path d="M12.25 3.5C6.59 3.5 2 7.39 2 12.2c0 2.74 1.5 5.18 3.85 6.77-.14.6-.5 1.85-.57 2.13-.1.36.26.36.42.26.12-.07 1.86-1.25 2.58-1.74 1.24.34 2.57.53 3.97.53 5.66 0 10.25-3.89 10.25-8.7S17.91 3.5 12.25 3.5Zm-5.1 10.76H5.08c-.22 0-.4-.18-.4-.4 0-.22.18-.4.4-.4h1.23L4.9 11.47a.56.56 0 0 1-.06-.57c.08-.18.26-.3.46-.3h2.08c.22 0 .4.18.4.4 0 .22-.18.4-.4.4H6.2l1.4 2a.56.56 0 0 1 .06.57.5.5 0 0 1-.46.3Zm2.6-.1c0 .22-.18.4-.4.4s-.4-.18-.4-.4v-3.27c0-.22.18-.4.4-.4s.4.18.4.4v3.27Zm4.14.4c-.12 0-.23-.05-.31-.14l-1.8-2.2v1.94c0 .22-.18.4-.4.4s-.4-.18-.4-.4v-3.27c0-.18.11-.33.28-.38.17-.05.35 0 .46.14l1.8 2.2v-1.96c0-.22.18-.4.4-.4s.4.18.4.4v3.27a.4.4 0 0 1-.3.39.5.5 0 0 1-.13.01Zm4.36-.01h-2.3c-.22 0-.4-.18-.4-.4v-3.27c0-.22.18-.4.4-.4s.4.18.4.4v2.87h1.9c.22 0 .4.18.4.4 0 .22-.18.4-.4.4Z" />
              </svg>
              <span>Chat Zalo</span>
            </a>
            <a
              href="https://www.facebook.com/autoshopee.com.vn"
              target="_blank"
              rel="noopener noreferrer"
              class="promo-popup-contact"
            >
              <a-icon
                type="message"
                class="promo-popup-contact-icon"
              />
              <span>Chat Facebook</span>
            </a>
            <a
              :href="phoneHref"
              class="promo-popup-contact"
            >
              <a-icon
                type="phone"
                class="promo-popup-contact-icon"
              />
              <span>0904.628.850</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getActivePopup } from '../api/utils/pricing';

// Feature flag: Subcheo follow popup disabled (keep code, flip to true to re-enable)
const PROMO_POPUP_ENABLED = false;

export default {
  name: 'PromoPopup',

  data() {
    return {
      visible: false,
      popup: null,
      timer: null,
    };
  },

  mounted() {
    if (!PROMO_POPUP_ENABLED) {
      return;
    }
    this.timer = setTimeout(async () => {
      try {
        const popup = await getActivePopup();
        if (!popup) {
          return;
        }
        try {
          const storageKey = `promo-popup:${popup.code}`;
          const lastShown = localStorage.getItem(storageKey);
          const now = Date.now();
          if (lastShown && now - Number(lastShown) < 24 * 60 * 60 * 1000) {
            return;
          }
          localStorage.setItem(storageKey, String(now));
        } catch (storageError) {
          return;
        }
        this.popup = popup;
        this.visible = true;
        window.addEventListener('keydown', this.handleKeyDown);
      } catch (error) {
        console.error({
          msg: 'PROMO_POPUP_LOAD',
          error,
        });
      }
    }, 1500);
  },

  beforeDestroy() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    window.removeEventListener('keydown', this.handleKeyDown);
  },

  methods: {
    formatNumber(n) {
      if (n === null || n === undefined || Number.isNaN(Number(n))) {
        return '';
      }
      return Number(n).toLocaleString('vi-VN');
    },
    formatPrice(n) {
      return `${this.formatNumber(n)}đ`;
    },
    close() {
      this.visible = false;
      window.removeEventListener('keydown', this.handleKeyDown);
    },
    handleKeyDown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        this.close();
      }
    },
  },

  computed: {
    showFollowRange() {
      return Boolean(
        this.popup
        && this.popup.minFollow !== null
        && this.popup.maxFollow !== null
        && this.popup.minFollow < this.popup.maxFollow,
      );
    },
    hasBenefits() {
      return Boolean(
        this.popup
        && (this.showFollowRange || (this.popup.maxDiscount && this.popup.maxDiscount > 0)),
      );
    },
    zaloHref() {
      return this.configApp && this.configApp.contact && this.configApp.contact.zalo
        ? this.configApp.contact.zalo
        : 'https://zalo.me/2141564390855067259';
    },
    phoneHref() {
      const phone = this.configApp && this.configApp.contact && this.configApp.contact.phone
        ? this.configApp.contact.phone
        : '0904628850';
      return `tel:${phone}`;
    },
  },
};
</script>

<style scoped>
.promo-popup {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  font-family: var(--font-family);
}

.promo-popup-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 15, 16, 0.6);
}

.promo-popup-dialog {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 820px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  display: grid;
  grid-template-columns: 300px 1fr;
  background: #fff;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
}

@media (prefers-reduced-motion: no-preference) {
  .promo-popup-dialog {
    animation: promoPopupEnter 0.24s cubic-bezier(0.16, 1, 0.3, 1);
  }
}

@keyframes promoPopupEnter {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.promo-popup-close {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-full);
  background: #fff;
  color: var(--text-secondary);
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color var(--transition-base), border-color var(--transition-base);
}

.promo-popup-close:hover {
  color: var(--primary-color);
  border-color: var(--primary-color);
}

.promo-popup-close:focus-visible,
.promo-popup-cta:focus-visible,
.promo-popup-contact:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

.promo-popup-hero {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2xl);
  padding: 32px 28px;
  background: var(--gradient-brand);
  color: #fff;
  border-top-left-radius: inherit;
  border-bottom-left-radius: inherit;
}

.promo-popup-hero.has-banner {
  gap: 14px;
  padding-top: 0;
}

.promo-popup-hero.has-banner .promo-popup-hero-main {
  margin-top: 0;
}

.promo-popup-hero.has-banner .promo-popup-hero-value {
  font-size: 3.4rem;
}

.promo-popup-banner {
  display: block;
  width: calc(100% + 56px);
  max-width: none;
  height: auto;
  margin: 0 -28px -8px;
  aspect-ratio: 6 / 5;
  object-fit: cover;
  object-position: center top;
  border-top-left-radius: inherit;
}

.promo-popup-eyebrow {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.9;
}

.promo-popup-hero-main {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: auto;
}

.promo-popup-hero-value {
  font-size: 3.5rem;
  font-weight: var(--font-weight-bold);
  line-height: 1;
  letter-spacing: -0.03em;
}

.promo-popup-hero-value--text {
  font-size: var(--font-size-2xl);
}

.promo-popup-hero-label {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
}

.promo-popup-benefits {
  list-style: none;
  margin: 0 0 auto;
  padding: 20px 0 0;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: var(--font-size-sm);
  line-height: 1.45;
}

.promo-popup-benefits li {
  display: flex;
  gap: 10px;
  align-items: baseline;
}

.promo-popup-benefit-icon {
  font-size: 0.8rem;
  flex-shrink: 0;
}

.promo-popup-body {
  display: flex;
  flex-direction: column;
  padding: 36px 32px 28px;
}

.promo-popup-title {
  margin: 0 44px 10px 0;
  font-size: 1.625rem;
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-tight);
  letter-spacing: -0.02em;
  color: var(--text-primary);
}

.promo-popup-desc {
  margin: 0 0 var(--spacing-xl);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  color: var(--text-secondary);
}

.promo-popup-plans {
  list-style: none;
  margin: 0 0 var(--spacing-xl);
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.promo-popup-plan {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 14px 14px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  background: #fff;
  transition: border-color var(--transition-base);
}

.promo-popup-plan:hover {
  border-color: var(--primary-color);
}

.promo-popup-plan.is-featured {
  border-color: var(--primary-color);
  background: var(--primary-light);
}

.promo-popup-plan-flag {
  position: absolute;
  top: -10px;
  left: 12px;
  padding: 3px 8px;
  border-radius: var(--border-radius-full);
  background: var(--primary-color);
  color: #fff;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.promo-popup-plan-name {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.promo-popup-plan-follow {
  font-size: 1.625rem;
  font-weight: var(--font-weight-bold);
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: var(--text-primary);
}

.promo-popup-plan-follow--text {
  font-size: var(--font-size-base);
}

.promo-popup-plan-unit {
  font-size: var(--font-size-xs);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-tertiary);
  font-weight: var(--font-weight-medium);
}

.promo-popup-plan-pricing {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.promo-popup-plan-price {
  font-size: 1.15rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: -0.01em;
  color: var(--text-primary);
}

.promo-popup-plan-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-xs);
}

.promo-popup-plan.is-featured .promo-popup-plan-unit,
.promo-popup-plan.is-featured .promo-popup-plan-meta del {
  color: var(--text-secondary);
}

.promo-popup-plan-meta del {
  color: var(--text-tertiary);
}

.promo-popup-plan-off {
  padding: 1px 6px;
  border-radius: var(--border-radius-full);
  background: var(--discount-bg);
  color: var(--discount-text);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
}

.promo-popup-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: auto;
}

.promo-popup-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 52px;
  padding: 0 24px;
  border-radius: var(--border-radius-md);
  background: var(--primary-color);
  color: #fff;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  text-decoration: none;
  transition: background-color var(--transition-base);
}

.promo-popup-cta:hover {
  background: var(--primary-hover);
  color: #fff;
}

.promo-popup-note {
  margin-top: 6px;
  font-size: 0.82rem;
  color: var(--text-secondary);
}

.promo-popup-contacts {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  width: 100%;
}

.promo-popup-contact {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  background: #fff;
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  text-decoration: none;
  white-space: nowrap;
  transition: border-color var(--transition-base), color var(--transition-base);
}

.promo-popup-contact:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.promo-popup-contact-icon {
  color: var(--primary-color);
  flex-shrink: 0;
}

@media (max-width: 640px) {
  .promo-popup {
    padding: 12px;
  }

  .promo-popup-dialog {
    grid-template-columns: 1fr;
    max-height: calc(100vh - 24px);
  }

  .promo-popup-hero {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 6px 14px;
    padding: 20px 56px 18px 18px;
    border-bottom-left-radius: 0;
    border-top-right-radius: inherit;
  }

  .promo-popup-eyebrow {
    flex-basis: 100%;
  }

  .promo-popup-hero-main {
    flex-direction: row;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 4px 10px;
  }

  .promo-popup-hero-value {
    font-size: 2.4rem;
  }

  .promo-popup-hero-label {
    font-size: 0.95rem;
  }

  .promo-popup-banner,
  .promo-popup-benefits {
    display: none;
  }

  .promo-popup-body {
    padding: 20px 16px 16px;
  }

  .promo-popup-title {
    margin-right: 0;
    font-size: 1.25rem;
  }

  .promo-popup-desc {
    margin-bottom: 16px;
    font-size: var(--font-size-xs);
  }

  .promo-popup-plans {
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
    margin-bottom: 16px;
  }

  .promo-popup-plan {
    gap: 8px;
    padding: 14px 6px 10px;
  }

  .promo-popup-plan-flag {
    top: -8px;
    left: 6px;
    padding: 2px 4px;
    font-size: 0.6rem;
  }

  .promo-popup-plan-follow {
    font-size: 1.125rem;
  }

  .promo-popup-plan-unit {
    font-size: 0.65rem;
  }

  .promo-popup-plan-price {
    font-size: 0.875rem;
  }

  .promo-popup-plan-meta {
    gap: 3px;
    font-size: 0.65rem;
  }

  .promo-popup-plan-off {
    padding: 0 4px;
    font-size: 0.65rem;
  }

  .promo-popup-contacts {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .promo-popup-contact {
    min-height: 40px;
    font-size: var(--font-size-xs);
  }
}
</style>
